import dayjs from "dayjs";
import "dayjs/locale/fr";
import { parse, isValid } from "date-fns";
import { UserType } from "../../types/user";
import { v4 as uuidv4 } from "uuid"; // Import UUID generator

// functions to format dates

export const formatToTimestamp = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0"); // Get month (0-indexed, so add 1)
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const seconds = String(d.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const parseDate = (dateString) => {
    const formats = [
        "M/d/yyyy, h:mm:ss a",
        "d/MM/yyyy, HH:mm:ss",
        "yyyy-MM-dd'T'HH:mm:ss",
        "yyyy-MM-dd'T'HH:mm:ssX",
        "dd/MM/yyyy HH:mm:ss",
        "MM/d/yyyy h:mm:ss a",
        "MMMM yyyy",
        "EEEE, MMMM d, yyyy h:mm:ss a",
        "yyyy-MM-dd HH:mm:ss",
    ];
    for (const format of formats) {
        const parsedDate = parse(dateString, format, new Date());
        if (isValid(parsedDate)) {
            return parsedDate;
        } else {
            console.log("Invalid date for format:", format);
        }
    }
    throw new Error("Unable to parse date");
};

export const formatMeetingDate = (date: string, locale = "en") => {
    if (!date) return "";

    return dayjs(date)
        .locale(locale)
        .format("dddd, DD MMMM YYYY ⋅ HH:mm")
        .replace(/\b\p{L}/u, (c) => c.toUpperCase());
};

// functions to format for calendar

export const formatSingleEvent = (object) => {
    return {
        title: object.title,
        description: object.description || null,
        client: object.client,
        meeting_id: object.meeting_id,
        meeting_type_id: object.meeting_type_id,
        start: object.start_date,
        end: object.end_date,
        is_visited: object.is_visited,
        is_organizer: object.is_organizer,
        type: object.meeting_type_id,
        attendees: object.attendees,
        is_recurring: object.is_recurring,
        recurring_id: object.recurring_id,
        is_lead: object.client ? object.client.is_lead : false,
        user: object.user,
        user_id: object.user?.user_id || object.user_id,
        lat: object.lat,
        long: object.long,
        address_1: object.address_1,
        zip_code: object.zip_code,
        city: object.city,
        country: object.country,
    };
};

export const formatRoutingEvent = (data) => {
    return {
        ...formatSingleEvent(data),
        is_lead: data.client?.is_lead ?? false,
        alert: data.client?.alert ?? false,
        client_id: data.client?.client_id ?? null,
        location: {
            lat: data.lat,
            lng: data.long,
        },
        time: {
            start: new Date(data.start_date).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
            }),
            end: new Date(data.end_date).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
            }),
        },
        date: data.start_date.split(" ")[0],
        distance: "0 km", // Placeholder for distance
        duration: "1 min", // Placeholder for duration
        db_key_path: data.db_key_path ?? null, // db_key_path: The key of indexBD data
    };
};

// functions to format events for modalfo
export const blankMeetingData = () => {
    return {
        title: "",
        start: "",
        meeting_id: "",
        end: "",
        client: null,
        client_id: null,
        description: "",
        location: "",
        lat: "",
        long: "",
        address_1: "",
        zip_code: "",
        city: "",
        country: "",
    };
};

const addMinutes = (date, minutes) => {
    return new Date(date.getTime() + minutes * 60000);
};

export const existingMeetingData = (initialEventData, detailsTrue, user) => {
    return {
        meeting_id: uuidv4(),
        title: initialEventData.title === "null" ? "" : initialEventData.title,
        description: initialEventData.description,
        start_date: initialEventData.start
            ? formatToTimestamp(initialEventData.start)
            : null,
        end_date: initialEventData.start
            ? !detailsTrue && initialEventData.user.user_id === user.user_id
                ? formatToTimestamp(
                      addMinutes(new Date(initialEventData.start), 30)
                  )
                : formatToTimestamp(initialEventData.end)
            : null,
        user: user,
        user_id: user.user_id, // Replace with the user's ID if applicable
        meeting_type_id: initialEventData.meeting_type_id
            ? initialEventData.meeting_type_id
            : "",
        is_visited: false,
        client: initialEventData.client || null,
        attendees: initialEventData.attendees || [],
        is_recurring: initialEventData.is_recurring,
        recurrence_type: "",
        recurrence_interval: "",
        recurrence_end_date: null,
        recurring_id: initialEventData.recurring_id,
        lat: initialEventData.lat,
        long: initialEventData.long,
        address_1: initialEventData.address_1,
        zip_code: initialEventData.zip_code,
        city: initialEventData.city,
        country: initialEventData.country,
        is_lead: initialEventData.is_lead,
    };
};

export const formatNewMeeting = (date: Date | null, user: UserType) => {
    const finalDate = date ? date : new Date();
    return {
        title: "",
        client: null,
        start: finalDate,
        end: new Date(finalDate).setHours(finalDate.getHours() + 1),
        meeting_id: "",
        location: "",
        meeting_type_id: "",
        attendees: [],
        user: user,
        description: "",
        lat: "",
        long: "",
        address_1: "",
        zip_code: "",
        city: "",
        country: "",
    };
};

export const formatExistingMeeting = (object) => {
    return {
        title: object.title || null,
        client: object.client,
        description: object.description || null,
        start: object.start || null,
        end: object.end || null,
        user: object.user,
        user_id: object.user_id,
        meeting_type_id: object.meeting_type_id || object.type,
        meeting_id: object.meeting_id,
        location: object.location || "",
        attendees: object.attendees || [],
        is_recurring: object.is_recurring,
        recurring_id: object.recurring_id,
        lat: object.lat,
        long: object.long,
        address_1: object.address_1,
        zip_code: object.zip_code,
        city: object.city,
        country: object.country,
        is_lead: object.is_lead,
    };
};

export const formatClientMeeting = (clientInfo, user) => {
    return {
        ...formatNewMeeting(null, user),
        is_organizer: true,
        client: {
            client_name: clientInfo.client_name,
            client_id: clientInfo.client_id,
            alert: clientInfo.next_best_actions.length > 0,
            is_lead: clientInfo.is_lead,
        },
        lat: clientInfo.lat,
        long: clientInfo.long,
        address_1: clientInfo.address_1,
        zip_code: clientInfo.zip_code,
        city: clientInfo.city,
        country: clientInfo.country,
    };
};

// open modal functions

export const formatMeetingToToday = (
    currentDate,
    setNewEventData,
    user,
    setDetailsTrue
) => {
    const now = new Date();

    const startTime = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate(),
        now.getHours(),
        now.getMinutes()
    );
    setNewEventData(formatNewMeeting(startTime, user));
    setDetailsTrue(false);
};
